import { useState, useRef, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

function fmtBytes(b) {
  if (!b) return ''
  return b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`
}

function FileIcon({ contentType }) {
  if (contentType?.includes('pdf')) return (
    <svg className="w-3.5 h-3.5 text-red-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v1H8v-1zm0 3h5v1H8v-1zm0-6h3v1H8v-1z" />
    </svg>
  )
  if (contentType?.startsWith('image/')) return (
    <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
  return (
    <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )
}

export default function ItemAttachments({
  entityType,
  projectId,
  quoteId,
  itemNum,
  allAttachments = [],
  onAttachmentAdded,
  onAttachmentDeleted,
  canUpload = false,
  profileId,
}) {
  const [uploading, setUploading]       = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)
  const [deletingId, setDeletingId]     = useState(null)
  const [hasCamera, setHasCamera]       = useState(false)
  const fileRef   = useRef(null)
  const cameraRef = useRef(null)

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices()
      .then(devices => setHasCamera(devices.some(d => d.kind === 'videoinput')))
      .catch(() => {})
  }, [])

  const attachments = allAttachments.filter(a =>
    a.entity_type === entityType &&
    (entityType === 'quote_item' ? a.quote_id === quoteId : a.project_id === projectId) &&
    a.item_num === String(itemNum)
  )

  async function handleDownload(att) {
    // Open blank window synchronously so iOS Safari doesn't block the popup
    const win = window.open('', '_blank')
    setDownloadingId(att.id)
    try {
      const { data, error } = await supabase.storage
        .from('item-attachments')
        .createSignedUrl(att.storage_path, 120)
      if (error) throw error
      win.location.href = data.signedUrl
    } catch (err) {
      win.close()
      alert('Download failed: ' + err.message)
    } finally {
      setDownloadingId(null)
    }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const entityId = entityType === 'quote_item' ? quoteId : projectId
      const path     = `${entityType}/${entityId}/${itemNum}/${Date.now()}_${safeName}`

      const { error: upErr } = await supabase.storage
        .from('item-attachments')
        .upload(path, file)
      if (upErr) throw upErr

      const { data: row, error: insErr } = await supabase
        .from('item_attachments')
        .insert({
          entity_type:            entityType,
          project_id:             entityType === 'project_item' ? projectId : null,
          quote_id:               entityType === 'quote_item'   ? quoteId   : null,
          item_num:               String(itemNum),
          storage_path:           path,
          filename:               file.name,
          file_size:              file.size,
          content_type:           file.type || null,
          uploaded_by_profile_id: profileId ?? null,
        })
        .select('*')
        .single()
      if (insErr) throw insErr

      onAttachmentAdded?.(row)
    } catch (err) {
      alert('Upload failed: ' + err.message)
    } finally {
      setUploading(false)
      if (fileRef.current)   fileRef.current.value   = ''
      if (cameraRef.current) cameraRef.current.value = ''
    }
  }

  async function handleDelete(att) {
    if (!window.confirm(`Remove "${att.filename}"?`)) return
    setDeletingId(att.id)
    try {
      await supabase.storage.from('item-attachments').remove([att.storage_path])
      const { error } = await supabase.from('item_attachments').delete().eq('id', att.id)
      if (error) throw error
      onAttachmentDeleted?.(att.id)
    } catch (err) {
      alert('Delete failed: ' + err.message)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="px-4 py-3">
      {attachments.length > 0 && (
        <div className="space-y-1.5 mb-2.5">
          {attachments.map(att => (
            <div key={att.id} className="flex items-center gap-2 group">
              <FileIcon contentType={att.content_type} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-700 truncate">{att.filename}</div>
                {att.file_size != null && (
                  <div className="text-[10px] text-gray-400">{fmtBytes(att.file_size)}</div>
                )}
              </div>
              <button
                onClick={() => handleDownload(att)}
                disabled={downloadingId === att.id}
                className="shrink-0 text-brand-red hover:text-brand-red-dark disabled:opacity-50"
                title="Download"
              >
                {downloadingId === att.id ? (
                  <span className="text-[10px] text-gray-400">…</span>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
              </button>
              {canUpload && (
                <button
                  onClick={() => handleDelete(att)}
                  disabled={deletingId === att.id}
                  className="shrink-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 disabled:opacity-50 transition-opacity"
                  title="Remove"
                >
                  {deletingId === att.id ? (
                    <span className="text-[10px] text-gray-400">…</span>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 && !canUpload && (
        <div className="text-xs text-gray-400 italic">No attachments</div>
      )}

      {canUpload && (
        <>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
          <div className="flex gap-2 mt-1 flex-wrap">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 transition-colors disabled:opacity-50"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              {uploading ? 'Uploading…' : 'Attach file'}
            </button>
            {hasCamera && (
              <button
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 transition-colors disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Take photo
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
